import LargeCheckCircle from '@mds/icons/CheckCircle';
import LargeWarningCircle from '@mds/icons/WarningCircle';
import Upload from '@mds/icons/Upload';
import UserInterfaceBin from '@mds/icons/Trash';
import { Box, IconButton, Paper, Typography } from '@mui/material';

import React, {
    ChangeEvent,
    DragEvent,
    MouseEvent,
    useEffect,
    useRef,
    useState,
} from 'react';

import { Grid } from '@mui/material';
import { Button } from '@mui/material';

interface LocalFileProps {
    title?: string;
    description?: React.ReactNode;
    accept?: string;
    onFileChanged: (file?: File) => void;
    error?: boolean;
}

export const LocalFile = ({
    title = 'Drag file or click here to upload',
    description,
    accept,
    onFileChanged,
    error,
}: LocalFileProps) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [draghover, setDragHover] = useState(false);
    const [formatError, setFormatError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [file, setFile] = useState<File>();

    useEffect(() => {
        onFileChanged(file);
    }, [file]);

    const choose = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        fileRef.current?.click();
    };

    const addFile = (e: ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (file) {
            proceedFile(file);
        }
    };

    const drop = (e: DragEvent<HTMLElement>) => {
        setDragHover(false);
        e.stopPropagation();
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (file) {
            proceedFile(file);
        }
    };

    const proceedFile = (file: File) => {
        const found = !accept
            ? true
            : accept
                  .split(',')
                  .find((f) => file.name.endsWith(f) || file.type === f);
        if (found) {
            setFormatError(false);
            setSuccess(true);
            setFile(file);
        } else {
            setSuccess(false);
            setFormatError(true);
            setFile(undefined);
        }
    };

    const hover = (hover: boolean) => (e: MouseEvent<HTMLElement>) => {
        setDragHover(hover);
        e.stopPropagation();
        e.preventDefault();
    };

    const deleteFile = () => {
        if (fileRef && fileRef.current && fileRef.current.value)
            fileRef.current.value = '';
        setFile(undefined);
        setSuccess(false);
        setFormatError(false);
    };

    const message = success
        ? 'Upload complete'
        : formatError
          ? 'File format not supported'
          : title;

    const attrs = !success
        ? {
              onClick: choose,
              onDropCapture: drop,
              onDragOverCapture: hover(true),
              onDragLeaveCapture: hover(false),
          }
        : {};

    return (
        <>
            <input
                type="file"
                accept={accept}
                ref={fileRef}
                onChange={addFile}
                style={{ display: 'none' }}
            />
            <Box
                {...attrs}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3,
                    bgcolor: 'background.paper',
                    border: 2,
                    borderStyle: 'dashed',
                    borderColor: error
                        ? 'error.main'
                        : draghover
                          ? 'text.secondary'
                          : 'divider',
                    textAlign: 'center',
                    transition: 'border-color .3s ease-in-out',
                }}
            >
                <Grid container spacing={2} style={{ pointerEvents: 'none' }}>
                    <Grid size={12}>
                        {success ? (
                            <LargeCheckCircle color="success" />
                        ) : formatError ? (
                            <LargeWarningCircle color="error" />
                        ) : (
                            <Upload />
                        )}
                    </Grid>
                    <Grid size={12}>
                        <Grid container spacing={1}>
                            <Grid size={12}>
                                <Typography variant="body1">
                                    <strong>{message}</strong>
                                </Typography>
                            </Grid>
                            {description && !success && (
                                <Grid size={12}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {description}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid style={{ pointerEvents: 'all' }} size={12}>
                        {file ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: 'action.hover',
                                    p: 2,
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    '& > div': {
                                        flex: 1,
                                        overflow: 'hidden',
                                        '&::after': {
                                            content: '""',
                                            display: 'block',
                                            height: 3,
                                            mt: 1,
                                            bgcolor: 'primary.main',
                                        },
                                        '& p': {
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        },
                                    },
                                }}
                            >
                                <Box title={file.name}>
                                    <Typography variant="caption">
                                        <strong>{file.name}</strong>
                                    </Typography>
                                </Box>
                                <IconButton size="small" onClick={deleteFile}>
                                    <UserInterfaceBin fontSize="small" />
                                </IconButton>
                            </Paper>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => {}}
                            >
                                Choose file
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
